import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { COLORS, TYPOGRAPHY } from '../../lib/styleGuide';

const ACCENT_GRAY = '#757575';
const SUCCESS_GREEN = '#4CAF50';

const foodCategories = [
  { id: 'baked-goods', name: 'Baked Goods', icon: '🍞' },
  { id: 'meal-prep', name: 'Meal Prep', icon: '🥗' },
  { id: 'desserts', name: 'Desserts', icon: '🍰' },
  { id: 'ready-to-eat', name: 'Ready to Eat', icon: '🍽️' },
  { id: 'snacks', name: 'Snacks', icon: '🥨' },
  { id: 'drinks', name: 'Drinks', icon: '🥤' },
  { id: 'candy', name: 'Candy', icon: '🍬' },
  { id: 'vegan', name: 'Vegan', icon: '🌱' },
  { id: 'gluten-free', name: 'Gluten Free', icon: '🌾' },
  { id: 'keto', name: 'Keto', icon: '🥑' },
];

export default function ReviewLaunchStep({ data, onBack, isFirst, isLast }) {
  const [isLaunching, setIsLaunching] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  function getCategoryName(categoryId) {
    const category = foodCategories.find(cat => cat.id === categoryId);
    return category ? category.name : categoryId;
  }

  function formatAddress(address) {
    if (!address) return 'Not provided';
    const parts = [address.street, address.apt, address.city, address.state, address.zip].filter(Boolean);
    return parts.join(', ');
  }

  async function handleLaunch() {
    setIsLaunching(true);
    setError('');

    try {
      // Simulate API call to create shop
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Simulate 95% success rate
      if (Math.random() > 0.05) {
        // Success - redirect to seller hub or show success message
        alert('🎉 Your shop has been launched successfully! Welcome to the creator community!');
        navigate('/seller-hub');
      } else {
        throw new Error('Failed to launch shop. Please try again.');
      }
    } catch (err) {
      setError(err.message || 'Failed to launch shop. Please try again.');
    } finally {
      setIsLaunching(false);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24, minHeight: '80vh' }}>
      {/* Header Section */}
      <div>
        <h2 style={{ fontWeight: 700, fontSize: TYPOGRAPHY.ONBOARDING.H2, margin: 0, color: '#222' }}>Review & Launch</h2>
        <p style={{ color: ACCENT_GRAY, fontSize: TYPOGRAPHY.ONBOARDING.STEP_HEADING, margin: '10px 0 0 0' }}>
          Let's review your shop details before we launch. Make sure everything looks correct!
        </p>
      </div>

      {/* Shop Information */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 16, background: '#F8F9FA', borderRadius: 12 }}>
          <span style={{ fontSize: TYPOGRAPHY.ONBOARDING.STEP_HEADING }}>🏪</span>
          <div>
            <div style={{ fontWeight: 600, fontSize: TYPOGRAPHY.ONBOARDING.STEP_HEADING, color: '#222', marginBottom: 2 }}>
              Shop Name
            </div>
            <div style={{ fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION, color: ACCENT_GRAY }}>
              {data.shopName || 'Not provided'}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 16, background: '#F8F9FA', borderRadius: 12 }}>
          <span style={{ fontSize: TYPOGRAPHY.ONBOARDING.STEP_HEADING }}>📍</span>
          <div>
            <div style={{ fontWeight: 600, fontSize: TYPOGRAPHY.ONBOARDING.STEP_HEADING, color: '#222', marginBottom: 2 }}>
              Location
            </div>
            <div style={{ fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION, color: ACCENT_GRAY }}>
              {data.address ? `${data.address.street}, ${data.address.city}, ${data.address.state} ${data.address.zip}` : 'Not provided'}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 16, background: '#F8F9FA', borderRadius: 12 }}>
          <span style={{ fontSize: TYPOGRAPHY.ONBOARDING.STEP_HEADING }}>🍽️</span>
          <div>
            <div style={{ fontWeight: 600, fontSize: TYPOGRAPHY.ONBOARDING.STEP_HEADING, color: '#222', marginBottom: 8 }}>
              Food Categories
            </div>
            {data.foodCategories && data.foodCategories.length > 0 ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {data.foodCategories.map((category, index) => (
                  <span key={index} style={{
                    padding: '4px 8px',
                    background: COLORS.PRIMARY_GRADIENT_START,
                    color: '#fff',
                    borderRadius: 6,
                    fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION,
                    fontWeight: 500
                  }}>
                    {category}
                  </span>
                ))}
              </div>
            ) : (
              <span style={{ color: ACCENT_GRAY, fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION }}>No categories selected</span>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 16, background: '#F8F9FA', borderRadius: 12 }}>
          <span style={{ fontSize: TYPOGRAPHY.ONBOARDING.STEP_HEADING }}>📸</span>
          <div>
            <div style={{ fontWeight: 600, fontSize: TYPOGRAPHY.ONBOARDING.STEP_HEADING, color: '#222', marginBottom: 8 }}>
              Photos & Story
            </div>
            <div style={{ fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION, color: ACCENT_GRAY }}>
              {data.photos && data.photos.length > 0 ? `${data.photos.length} photos uploaded` : 'No photos uploaded'}
            </div>
            {data.story && (
              <div style={{ fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION, color: ACCENT_GRAY, marginTop: 4 }}>
                Story: {data.story.substring(0, 100)}...
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 16, background: '#F8F9FA', borderRadius: 12 }}>
          <span style={{ fontSize: TYPOGRAPHY.ONBOARDING.STEP_HEADING }}>💳</span>
          <div>
            <div style={{ fontWeight: 600, fontSize: TYPOGRAPHY.ONBOARDING.STEP_HEADING, color: '#222', marginBottom: 2 }}>
              Payment Setup
            </div>
            <div style={{ fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION, color: ACCENT_GRAY }}>
              {data.paymentSetup ? 'Payment processing configured' : 'Payment setup pending'}
            </div>
            <div style={{ minHeight: 22, fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION }}>
              {data.paymentSetup ? '✅ Ready to accept payments' : '⏳ Setup required'}
            </div>
          </div>
        </div>
      </div>

      {/* Error Message */}
      <div style={{ minHeight: 22, fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION }}>
        {error && <span style={{ color: '#F44336' }}>{error}</span>}
      </div>

      {/* Navigation Buttons */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        gap: 12, 
        marginTop: 'auto'
      }}>
        {!isFirst && (
          <button
            type="button"
            onClick={onBack}
            style={{
              background: '#fff',
              color: '#9CA3AF',
              border: 'none',
              borderRadius: 10,
              padding: '14px 32px',
              fontSize: TYPOGRAPHY.ONBOARDING.BUTTON_TEXT,
              fontWeight: 600,
              cursor: 'pointer',
              minHeight: 44,
              minWidth: 120,
              transition: 'all 0.2s',
            }}
            aria-label="Back"
          >
            Back
          </button>
        )}
        <button
          type="button"
          onClick={() => onBack && onBack()}
          style={{
            background: COLORS.PRIMARY_GRADIENT,
            color: '#fff',
            border: 'none',
            borderRadius: 10,
            padding: '14px 32px',
            fontSize: TYPOGRAPHY.ONBOARDING.BUTTON_TEXT,
            fontWeight: 600,
            cursor: 'pointer',
            minHeight: 44,
            minWidth: 120,
            transition: 'all 0.2s',
            marginLeft: 'auto'
          }}
          aria-label="Launch Shop"
        >
          <span style={{ fontSize: TYPOGRAPHY.ONBOARDING.STEP_HEADING }}>🚀</span> Launch My Shop!
        </button>
      </div>
    </div>
  );
} 